import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import backend from '../../services/backend';
import { CategoryMapScatter } from './CategoryMapScatter';
import { CategoryMapWorklist } from './CategoryMapWorklist';

function headerSummary(args: {
  isLoading: boolean;
  isError: boolean;
  comparingCount?: number;
  candidateCount?: number;
  totalClusters?: number;
}): string {
  if (args.isLoading) {
    return 'Loading…';
  }
  if (args.isError) {
    return 'Couldn’t load';
  }
  if (args.comparingCount && args.comparingCount > 0) {
    return `Comparing ${args.comparingCount} categories`;
  }
  return `${args.candidateCount ?? 0} candidates · ${args.totalClusters ?? 0} clusters`;
}

export default function CategoryMislabelMap({
  emphasizedCategoryIds = null,
  selectedCategoryIds = null,
  onClearSelection,
}: {
  /** When set, dots/rows outside these categories dim (scatter) or hide (worklist). null = no filter. */
  emphasizedCategoryIds?: Set<number> | null;
  /** When non-empty, render ONLY these categories' clusters (compare mode). null/empty = show all. */
  selectedCategoryIds?: Set<number> | null;
  /** Clears the table selection; surfaced as a "Clear" link while comparing. */
  onClearSelection?: () => void;
}) {
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['categoryClustersMap'],
    queryFn: () => backend.getCategoryClustersMap(),
  });

  // On a failed (re)fetch react-query keeps the last successful `data`; drop it so the
  // scatter doesn't show stale points while the header/worklist report the error.
  const points = isError ? [] : (data?.points ?? []);
  // Compare mode: when categories are checked in the table, show ONLY their clusters.
  const visiblePoints =
    selectedCategoryIds && selectedCategoryIds.size > 0
      ? points.filter((point) => selectedCategoryIds.has(point.categoryId))
      : points;
  // Each dot / worklist row is a cluster (point.id); open its cluster page in a new tab
  // so the map stays open while you inspect.
  const goToCluster = useCallback(
    (clusterId: number) => window.open(`/category-clusters/${clusterId}`, '_blank', 'noopener'),
    [],
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-800 dark:shadow-zinc-950/30">
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-gray-900 dark:text-zinc-100">Category map</h3>
        <span className="text-xs text-gray-500 dark:text-zinc-400">
          {headerSummary({
            isLoading,
            isError,
            comparingCount: selectedCategoryIds?.size ?? 0,
            candidateCount: data?.candidateCount,
            totalClusters: data?.totalClusters,
          })}
          {!isLoading && !isError && selectedCategoryIds && selectedCategoryIds.size > 0 && onClearSelection && (
            <button
              type="button"
              onClick={onClearSelection}
              className="ml-2 text-indigo-600 hover:underline dark:text-indigo-400"
            >
              Clear
            </button>
          )}
        </span>
      </div>
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="lg:w-2/3">
          <CategoryMapScatter
            points={visiblePoints}
            emphasizedCategoryIds={emphasizedCategoryIds}
            hoveredId={hoveredId}
            onHover={setHoveredId}
            onSelect={goToCluster}
          />
        </div>
        <div className="lg:w-1/3">
          <CategoryMapWorklist
            points={visiblePoints}
            emphasizedCategoryIds={emphasizedCategoryIds}
            loading={isLoading}
            error={isError}
            hoveredId={hoveredId}
            onHover={setHoveredId}
            onSelect={goToCluster}
          />
        </div>
      </div>
    </div>
  );
}
